import {Link} from 'react-router-dom';
import React, {useEffect, useState} from "react";
import {websocket} from "../index";
import logo from './logo.png'
import screenshot from './esh_screenshot.png'

// GLOBAL CONSTANTS ----------------------------------------------------------------------------------------------------

export const DOMAIN = window.location.hostname;
export const BASE_URL_WEB = window.location.protocol + '//' + DOMAIN + (window.location.port === '' ? '' : ':' + window.location.port);
// The server serves this app, so the API lives on the same origin. Only a split dev setup needs the override.
export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || BASE_URL_WEB;
export const OPEN_SPOTIFY = 'https://open.spotify.com';
const HTTP_METHODS = new Map<string, string>([
    ['login', 'POST'],
    ['stats', 'GET'],
    ['entityStats', 'GET'],
    ['data', 'POST'],
    ['search', 'GET'],
    ['session', 'GET'],
]);

// Body returned with a 401 when the user's Spotify authorization died, as opposed to a bad session token
export const REAUTH_REQUIRED = 'reauth_required';

// PROPS TYPES
interface NavButtonProps {
    url: string,
    text: string,
}

export interface InfoProps {
    text: string | React.ReactElement,
}

interface ExtendedStreamingInfoProps {
    callback: () => void,
}

// ELEMENTS COMMON TO EVERY PAGE ---------------------------------------------------------------------------------------

export const Header = () => {
    const [navButtons, setNavButtons] = useState<React.ReactElement>();

    const NavButton = (props: NavButtonProps) => {
        return (
            <Link to={props.url} className={'text-mm-cyan no-underline'}>
                <div className={'py-[0.65rem] px-[1.3rem] rounded-[0.65rem] hover:bg-mm-cyan hover:text-mm-dark hover:cursor-pointer hover:transition-all hover:duration-[800ms]'}>
                    {props.text}
                </div>
            </Link>
        );
    }

    const HamburgerMenu = () => {
        const [isOpen, setIsOpen] = useState(false);

        const toggle = () => {
            setIsOpen(!isOpen);
        }

        useEffect(() => {
            document.addEventListener('click', (event: MouseEvent) => {
                const target = event.target as HTMLElement;
                if (!target.classList.toString().includes('hamburger')) {
                    setIsOpen(false);
                }
            });
        }, []);

        return (
            <div className={'relative inline-block'}>
                <div className={'hamburger h-8 w-8 bg-transparent flex flex-col justify-around cursor-pointer'} onClick={toggle}>
                    <div className={'hamburger h-[0.2rem] w-8 bg-mm-cyan rounded-[0.2rem]'}/>
                    <div className={'hamburger h-[0.2rem] w-8 bg-mm-cyan rounded-[0.2rem]'}/>
                    <div className={'hamburger h-[0.2rem] w-8 bg-mm-cyan rounded-[0.2rem]'}/>
                </div>
                {isOpen && (
                    <div className={'hamburger absolute top-full right-0 p-2 bg-mm-dark shadow-md border border-mm-cyan rounded z-[1]'}>
                        <ul className={'list-none p-0 m-0'}>
                            <Link to={'/'} className={'text-mm-cyan no-underline'}><li className={'hamburger p-2 cursor-pointer rounded-[0.2rem] hover:bg-mm-text-hover hover:text-mm-dark'} onClick={toggle}>HOME</li></Link>
                            <Link to={'/stats'} className={'text-mm-cyan no-underline'}><li className={'hamburger p-2 cursor-pointer rounded-[0.2rem] hover:bg-mm-text-hover hover:text-mm-dark'} onClick={toggle}>STATS</li></Link>
                            <Link to={'/account'} className={'text-mm-cyan no-underline'}><li className={'hamburger p-2 cursor-pointer rounded-[0.2rem] hover:bg-mm-text-hover hover:text-mm-dark'} onClick={toggle}>ACCOUNT</li></Link>
                        </ul>
                    </div>
                )}
            </div>
        );
    }

    // Dynamically change the navigation bar based on the orientation of the device.
    useEffect(() => {
        // The default navigation bar in landscape mode
        const navButtonsLandscape = (
            <div className={'flex flex-row p-[0.65rem] text-mm-cyan'}>
                <NavButton url={'/'} text={'HOME'}/>
                <NavButton url={'/stats'} text={'STATS'}/>
                <NavButton url={'/account'} text={'ACCOUNT'}/>
            </div>
        );
        // The navigation bar in portrait mode, defined below.
        const navButtonsPortrait: React.ReactElement = (
            <HamburgerMenu/>
        );
        const mediaQuery = window.matchMedia('(orientation: portrait)');
        setNavButtons(mediaQuery.matches ? navButtonsPortrait : navButtonsLandscape);

        const handleOrientationChange = (event: MediaQueryListEvent) => {
            setNavButtons(event.matches ? navButtonsPortrait : navButtonsLandscape);
        }

        mediaQuery.addEventListener('change', handleOrientationChange);
        return () => {
            mediaQuery.removeEventListener('change', handleOrientationChange);
        }
    }, []);

    return (
        <header className={'z-[1] top-0 left-0 p-[0.65rem] flex flex-row justify-between items-center'}>
            <div>
                <Link to={'/'}>
                    <img
                        className={'h-12 portrait:h-8 cursor-pointer'}
                        src={logo}
                        alt={'Main logo'}
                    />
                </Link>
            </div>
            {navButtons}
        </header>
    );
}

export const Footer = () => {
    const onClickTemp = () => {
        websocket.send('hello there from the client');
    }

    return (
        <footer className={'absolute bottom-0 left-0 right-0 text-center p-[0.7rem] text-base text-mm-cyan'}>
            <p>
                <span onClick={() => clearStorage()}>&copy;</span> (pending) <span onClick={() => logStorage()}>2023</span> Noah <span onClick={() => onClickTemp()}>Pratt</span> <span className={'text-mm-text'}>&#8226;</span>
                <Link to={'/privacy'} className={'text-mm-cyan no-underline'}> Privacy Policy</Link> <span className={'text-mm-text'}>&#8226;</span>
                <Link to={'/terms'} className={'text-mm-cyan no-underline'}> Terms of Service</Link> <span className={'text-mm-text'}>&#8226;</span>
                <Link to={'/about'} className={'text-mm-cyan no-underline'}> About</Link> <span className={'text-mm-text'}>&#8226;</span>
                <Link to={'/contact'} className={'text-mm-cyan no-underline'}> Contact Us</Link>
            </p>
        </footer>
    );
}

export const PrimaryInfo = (props: InfoProps) => {
    return (
        <div className={'text-center text-[4rem] portrait:text-[2.5rem] text-mm-text m-6'}>
            <b>{props.text}</b>
        </div>
    );
}

export const SecondaryInfo = (props: InfoProps) => {
    return (
        <div className={'text-center text-[1.9rem] text-mm-text m-6'}>
            {props.text}
        </div>
    );
}

export const ExtendedStreamingInfo = (props: ExtendedStreamingInfoProps) => {
    return (
        <div className={'fixed top-0 left-0 w-full h-full bg-black/70 flex justify-center items-center z-[9999]'}>
            <div className={'overflow-y-auto bg-mm-dark-2 p-4 rounded-lg text-mm-text flex flex-col justify-between items-center text-center w-[40%] portrait:w-[90%] [&>h1]:mt-0 [&>h1]:mb-4 [&>h1]:mx-auto'}>
                <div style={{width: '100%'}}>
                    <div className={'text-2xl font-bold'}>Want All-Time Stats?</div>
                    <div>To obtain your extended streaming history:<br/>
                        <div style={{textAlign: 'left'}}>
                            <div style={{marginBottom: '.5rem'}}><b>1.</b> Visit your <a href={'https://www.spotify.com/us/account/privacy/'} target={'_blank'} rel={'noreferrer'} className={'text-mm-cyan no-underline'}><u>Privacy Settings</u></a> on Spotify.<br/></div>
                            <div style={{marginBottom: '.5rem'}}><b>2.</b> Uncheck 'Account data' and select 'Extended streaming history' so that it looks like the screenshot below.<br/></div>
                            <div style={{marginBottom: '.5rem'}}><b>3.</b> Within 30 days, you will receive a .zip file via email (download it).<br/></div>
                            <div style={{marginBottom: '.5rem'}}><b>4.</b> Upload the .zip file here.<br/></div>
                            <div><b>5.</b> Stats from your complete history will be available within a few minutes.<br/></div>
                        </div>
                    </div>
                    <img src={screenshot} alt={'An instructive screenshot'} className={'w-[80%] mt-4 rounded-[0.2rem] mx-auto'}/>
                </div>
                <div className={'bg-mm-green p-2 rounded-[0.6rem] inline-block text-mm-dark text-center hover:bg-mm-text hover:cursor-pointer mt-2 !mx-auto px-12'} onClick={props.callback}><b>OK</b></div>
            </div>
        </div>
    );
}

// LOGIN ELEMENTS ------------------------------------------------------------------------------------------------------

export const getMMToken = () => {
    return localStorage.getItem('mm-token');
}

// SPOTIFY RE-AUTHORIZATION --------------------------------------------------------------------------------------------

// Spotify expires refresh tokens six months after the user authorized the app, and refreshing does not
// extend that. Our own session token never expires, so being logged in here says nothing about whether
// we can still reach Spotify on the user's behalf. This asks the server, which is the only side that knows.

export interface SpotifySession {
    needsSpotifyReauth: boolean,
    spotifyAuthExpiringSoon: boolean,
    spotifyAuthExpiresAt: number | null,
    // When tracking stopped, or null if there is no unfilled gap. Outlives reconnection, because
    // reconnecting resumes tracking without recovering the listens missed in between.
    trackingPausedAt: number | null,
}

/** e.g. "March 3, 2026" -- used to tell the user exactly when their history stops. */
export const formatGapDate = (millis: number) => {
    return new Date(millis).toLocaleDateString(undefined, {year: 'numeric', month: 'long', day: 'numeric'});
}

export const getSpotifySession = async (): Promise<SpotifySession | undefined> => {
    const username = localStorage.getItem('username');
    const token = getMMToken();
    if (!username || !token) return undefined;

    try {
        const response = await fetch(BACKEND_URL + '/api/session/' + username, fetchInit('session', null, token));
        if (!response.ok) return undefined;
        return await response.json();
    } catch {
        // Never let a status check break the page; the banner just stays hidden.
        return undefined;
    }
}

interface SpotifyReauthBannerProps {
    session: SpotifySession | undefined,
    // Set on the Account page, where the upload control is already on screen and a link would be circular
    uploadIsOnThisPage?: boolean,
}

/**
 * Tells the user the state of their Spotify connection, and how to repair a gap in their history.
 *
 * Three states in priority order: expired (tracking has stopped), gap-only (reconnected but the missed
 * listens are still missing), and expiring soon. Reconnecting only resumes tracking going forward, so
 * the gap prompt deliberately outlives the reconnect -- that is the moment the user can actually act.
 *
 * Not auto-redirecting: re-authorizing is a full navigation off-site, so it should be the user's click.
 * Because the app is already approved and show_dialog is false, that click is usually invisible.
 */
export const SpotifyReauthBanner = (props: SpotifyReauthBannerProps) => {
    const session = props.session;
    const [showHowTo, setShowHowTo] = useState(false);

    if (!session) return null;

    const expired = session.needsSpotifyReauth;
    const hasGap = session.trackingPausedAt != null;
    if (!expired && !hasGap && !session.spotifyAuthExpiringSoon) return null;

    const since = session.trackingPausedAt != null ? formatGapDate(session.trackingPausedAt) : null;

    const GapInstructions = () => (
        <div className={'text-base mt-2 text-mm-text'}>
            {since
                ? <>No listens have been recorded since <b>{since}</b>. </>
                : <>Some of your listening history is missing. </>}
            Spotify does not let us recover them automatically, but you can fill the gap by uploading your
            extended streaming history{props.uploadIsOnThisPage ? ' below' : ''}.
            <span className={'text-mm-cyan cursor-pointer'} onClick={() => setShowHowTo(true)}> <u>How do I get that?</u></span>
            {!props.uploadIsOnThisPage && (
                <> Head to <Link to={'/account'} className={'text-mm-cyan'}><u>your account page</u></Link> to upload it.</>
            )}
        </div>
    );

    // Offer reconnection whenever it would help. Not in the gap-only state: there the connection is
    // already healthy and the only outstanding action is the upload.
    const showReconnect = expired || session.spotifyAuthExpiringSoon;

    let headline: string;
    let accent: string;
    if (expired) {
        headline = 'Your Spotify connection expired, so new listens are no longer being tracked. Your existing stats are safe.';
        accent = 'border-mm-red text-mm-red';
    } else if (hasGap) {
        headline = 'Spotify is reconnected and tracking has resumed, but there is still a gap in your history.';
        accent = 'border-mm-cyan text-mm-cyan';
    } else {
        headline = 'Your Spotify connection expires soon. Reconnect now to avoid a gap in your tracked listens.';
        accent = 'border-mm-cyan text-mm-cyan';
    }

    return (
        <>
            <div className={`mx-auto my-4 w-3/4 portrait:w-[90%] rounded-[0.5rem] border p-4 ${accent}`}>
                <div className={'flex flex-row portrait:flex-col gap-4 items-center justify-between'}>
                    <div className={'text-base'}>{headline}</div>
                    {showReconnect && (
                        <div
                            className={'bg-mm-green py-2 px-6 rounded-[0.6rem] text-mm-dark text-center whitespace-nowrap hover:bg-mm-text hover:cursor-pointer'}
                            onClick={() => spotifyAuth()}
                        >
                            <b>RECONNECT SPOTIFY</b>
                        </div>
                    )}
                </div>
                {hasGap && <GapInstructions/>}
            </div>
            {showHowTo && <ExtendedStreamingInfo callback={() => setShowHowTo(false)}/>}
        </>
    );
}

/** Fetches the Spotify session status once on mount. Returns undefined until it is known. */
export const useSpotifySession = () => {
    const [session, setSession] = useState<SpotifySession | undefined>(undefined);

    useEffect(() => {
        let active = true;
        getSpotifySession().then(result => {
            if (active) setSession(result);
        });
        return () => {
            active = false;
        };
    }, []);

    return session;
}

// USEFUL METHODS ------------------------------------------------------------------------------------------------------

export const useDebounce = (callback: any, delay: number) => {
    const [value, setValue] = useState(callback);

    useEffect(() => {
        const handler = setTimeout(() => {
            setValue(callback);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [callback, delay]);

    return value;
}

export const fetchInit = (endpoint: string, requestBody: BodyInit | null, token: string) => {
    if (HTTP_METHODS.get(endpoint) === 'GET') {
        return {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token
            }
        }
    } else {
        return {
            method: HTTP_METHODS.get(endpoint),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token
            },
            body: requestBody
        }
    }
}

export const getSpotifyAuthURL = () => {
    let client_id = '8b99139c99794d4b9e89b8367b0ac3f4';
    let redirect_uri = BASE_URL_WEB + '/spotify-landing';
    let state = Math.floor(Math.random() * 100000000); // random 8 digit number
    sessionStorage.setItem('state', state.toString());
    let responseType = 'code';
    let scope = 'user-read-playback-state ' +
        'playlist-read-private ' +
        'playlist-read-collaborative ' +
        'playlist-modify-private ' +
        'playlist-modify-public ' +
        'user-follow-read ' +
        'user-read-currently-playing ' +
        'user-read-playback-position ' +
        'user-read-email ' +
        'user-top-read ' +
        'user-read-recently-played ' +
        'user-read-private ' +
        'user-library-read';

    let show_dialog = 'false';
    let url = 'https://accounts.spotify.com/authorize';
    url += '?response_type=' + encodeURIComponent(responseType);
    url += '&client_id=' + encodeURIComponent(client_id);
    url += '&scope=' + encodeURIComponent(scope);
    url += '&show_dialog=' + encodeURIComponent(show_dialog);
    url += '&redirect_uri=' + encodeURIComponent(redirect_uri);
    url += '&state=' + encodeURIComponent(state);

    return url;
}

export const spotifyAuth = () => {
    window.location.href = getSpotifyAuthURL();
}

export const needsLogin = (): boolean => {
    return !localStorage.getItem('mm-token');
}

export const clearStorage = () => {
    localStorage.clear();
    sessionStorage.clear();
}

const logStorage = () => {
    console.log(localStorage);
    console.log(sessionStorage);
}
